import { supabase, getCurrentUserId } from './supabase';

export type ToothRecord = {
  tooth_number: number;
  kemik: '1' | '2' | '3';
  soket: 'e' | 'h';
  greft: 'e' | 'h';
  hacim: 'z' | 'y';
  healing?: 'e' | 'h';
  implant_brand: string;
  vdk: string;
  failed?: boolean;
  fail_date?: string | null;
  fail_days?: number | null;
};

export type Surgery = {
  id: string;
  patient_name: string;
  phone?: string;
  clinic?: string;
  operation_date: string;
  cinsiyet: 'k' | 'e';
  yas: '1' | '2' | '3' | '4';
  seker: '0' | '1' | '2';
  sigara: '0' | '1' | '2';
  teeth: ToothRecord[];
  completed?: boolean;
  created_at: string;
  updated_at: string;
};

export type TreatmentStep = {
  step_id: string;
  label: string;
  completed: boolean;
  completed_date: string | null;
  planned_date: string | null;
};

export type TreatmentPlan = {
  id: string;
  surgery_id: string;
  patient_name: string;
  operation_date: string;
  steps: TreatmentStep[];
  all_completed: boolean;
  created_at: string;
  updated_at: string;
};

export type PatientNote = {
  id: string;
  patient_id: string;
  note_date: string;
  note_type: 'general' | 'checkup' | 'complication' | 'treatment' | 'followup';
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
};

/* ─────────────────── Surgery operations ─────────────────── */
export const dbSurgeries = {
  getAll: async (): Promise<Surgery[]> => {
    const { data, error } = await supabase
      .from('surgeries')
      .select('*')
      .order('operation_date', { ascending: false });
    if (error) throw error;
    return (data ?? []) as Surgery[];
  },

  getById: async (id: string): Promise<Surgery | undefined> => {
    const { data, error } = await supabase
      .from('surgeries')
      .select('*')
      .eq('id', id)
      .single();
    if (error) return undefined;
    return data as Surgery;
  },

  count: async (): Promise<number> => {
    const { count, error } = await supabase
      .from('surgeries')
      .select('*', { count: 'exact', head: true });
    if (error) throw error;
    return count ?? 0;
  },

  insert: async (surgery: Omit<Surgery, 'id' | 'created_at' | 'updated_at'>): Promise<Surgery> => {
    const userId = await getCurrentUserId();
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('surgeries')
      .insert({ ...surgery, user_id: userId, created_at: now, updated_at: now })
      .select()
      .single();
    if (error) throw error;
    return data as Surgery;
  },

  update: async (id: string, data: Partial<Surgery>): Promise<void> => {
    const { error } = await supabase
      .from('surgeries')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  delete: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('surgeries')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};

/* ─────────────────── Patient note operations ─────────────────── */
export const dbNotes = {
  getAll: async (): Promise<PatientNote[]> => {
    const { data, error } = await supabase
      .from('patient_notes')
      .select('*')
      .order('note_date', { ascending: false });
    if (error) throw error;
    return (data ?? []) as PatientNote[];
  },

  getByPatientId: async (patientId: string): Promise<PatientNote[]> => {
    const { data, error } = await supabase
      .from('patient_notes')
      .select('*')
      .eq('patient_id', patientId)
      .order('note_date', { ascending: false });
    if (error) throw error;
    return (data ?? []) as PatientNote[];
  },

  insert: async (note: Omit<PatientNote, 'id' | 'created_at' | 'updated_at'>): Promise<PatientNote> => {
    const userId = await getCurrentUserId();
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('patient_notes')
      .insert({ ...note, user_id: userId, created_at: now, updated_at: now })
      .select()
      .single();
    if (error) throw error;
    return data as PatientNote;
  },

  update: async (id: string, data: Partial<PatientNote>): Promise<void> => {
    const { error } = await supabase
      .from('patient_notes')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  delete: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('patient_notes')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};

/* ─────────────────── Treatment Plan operations ─────────────────── */
export const dbTreatmentPlans = {
  getAll: async (): Promise<TreatmentPlan[]> => {
    const { data, error } = await supabase
      .from('treatment_plans')
      .select('*')
      .order('operation_date', { ascending: false });
    if (error) throw error;
    return (data ?? []) as TreatmentPlan[];
  },

  getBySurgeryId: async (surgeryId: string): Promise<TreatmentPlan | undefined> => {
    const { data, error } = await supabase
      .from('treatment_plans')
      .select('*')
      .eq('surgery_id', surgeryId)
      .maybeSingle();
    if (error) return undefined;
    return data as TreatmentPlan | undefined;
  },

  insert: async (plan: Omit<TreatmentPlan, 'id' | 'created_at' | 'updated_at'>): Promise<TreatmentPlan> => {
    const userId = await getCurrentUserId();
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('treatment_plans')
      .insert({ ...plan, user_id: userId, created_at: now, updated_at: now })
      .select()
      .single();
    if (error) throw error;
    return data as TreatmentPlan;
  },

  update: async (id: string, data: Partial<TreatmentPlan>): Promise<void> => {
    const { error } = await supabase
      .from('treatment_plans')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  delete: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('treatment_plans')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};
